// English UI messages — the source of truth for the Messages shape.
// Every other locale must satisfy `Messages` so a missing key is a type error.
export const en = {
  common: {
    cancel: 'Cancel',
    read: 'Read',
    remove: 'Remove',
    save: 'Save',
    saving: 'Saving…',
    close: 'Close',
    back: 'Back',
    goHome: 'Go home',
    error: (message: string) => `Error: ${message}`,
  },

  shelf: {
    signIn: 'Sign in',
    logout: 'Log out',
    credits: 'Credits',
    buyCredits: 'Buy credits',
    help: 'Help',
    dockHint: 'Move to look around · Scroll to zoom · Click to open',
    prevPage: 'Previous shelf page',
    nextPage: 'Next shelf page',
    byAuthor: (author: string) => `By ${author}`,
    removeBook: 'Remove book',
    removeTitle: 'Remove this book?',
    removeMessage: (title: string) =>
      `“${title}” will be removed from your shelf. This can't be undone.`,
    loadFailedTitle: "Couldn't load the shelf",
    loadFailedMessage: 'Check your connection and try again.',
    translationFailed: 'Translation failed',
    translating: 'Translating…',
    translatingProgress: (done: number, total: number, pct: number) =>
      `Translating… ${done}/${total} chapters (${pct}%)`,
    extractingGlossary: 'Extracting glossary…',
    preparingArtwork: 'Preparing cover and spine…',
    safeToClose:
      'You can safely close this page — the book will be ready when you return.',
    completed: '✓ Completed',
    percentRead: (pct: number) => `${pct}% read`,
    notStarted: 'Not started',
    uploadedTitle: 'Book uploaded!',
    uploadedOriginalOnly: 'Preparing cover and spine — original language only.',
    uploadedTranslating:
      'Translation is in progress — you can safely close this page.',
  },

  closet: {
    loadingQuips: [
      'Sweeping the dust off the shelves…',
      'Straightening the spines…',
      'Lighting the reading lamp…',
      'Waking the librarian…',
    ],
    bookInfo: 'Book info',
    formLabelNo: 'Form 3',
    formLabelKind: 'Shelf Label',
    labelField: 'label',
    labelPlaceholder: 'e.g. Sci-fi, To read…',
    labelHint: 'this slip clips onto the shelf edge',
    editShelfLabel: 'Edit shelf label',
    titleField: 'title',
    authorField: 'author',
    statusField: 'status',
    progressField: 'progress',
    translatingCount: (done: number, total: number) =>
      `Translating… ${done}/${total}`,
    extractingGlossary: 'Extracting glossary…',
    translating: 'Translating…',
    preparingArtwork: 'Preparing cover and spine…',
    completedStamp: 'Completed',
    shelfTheme: 'Shelf color',
    shelfThemeNames: {
      walnut: 'Walnut',
      white: 'Whitewash',
      steel: 'Steel',
    } as Record<string, string>,
  },

  upload: {
    dialogUpload: 'Upload book',
    dialogAddHere: 'Add book here',
    dialogConfirm: 'Confirm book upload',
    form1No: 'Form 1',
    form1Kind: 'Acquisition',
    form2No: 'Form 2',
    form2Kind: 'Catalogue',
    loginRequired: 'Please sign in to upload books',
    estimateFailed: "Couldn't read this book",
    reEstimateFailed: "Couldn't update the estimate",
    uploading: 'Uploading…',
    startingTranslation: 'Starting translation…',
    analyzing: 'analyzing book…',
    uploadFailed: 'Upload failed',
    networkError:
      'Network error while uploading. Please try again. If it keeps failing, let us know on Discord (in the ? menu).',
    insufficientCredits: 'Insufficient credits',
    requiredField: 'required',
    availableField: 'available',
    needField: 'need',
    moreAmount: (amount: string) => `${amount} more`,
    needMore: (amount: string) => `need ${amount} more`,
    balanceField: 'balance',
    afterField: 'after',
    creditsUnit: 'credits',
    shelfField: 'shelf',
    selectedShelf: 'Selected shelf',
    slotPosition: (row: number, col: number) => `Row ${row}, bay ${col}`,
    modeAria: 'Upload mode',
    translateThisBook: 'Translate this book',
    bilingualNote: 'bilingual',
    importOriginalOnly: 'Import original only',
    freeNote: 'free',
    languagesField: 'languages',
    sourceLanguage: 'Source language',
    targetLanguage: 'Target language',
    sameLanguageWarning:
      'source and target language are the same — change one to continue',
    confirmTranslate: 'Confirm & translate',
    importWithoutTranslation: 'Import without translation',
    affixHere: 'drop or pick a book',
    acceptedFormats: 'epub · mobi · azw3',
    unsupportedFile: 'Only EPUB, MOBI and AZW3 files can be uploaded',
    chooseAgain: 'Please choose the book again',
    languagesNextForm: 'languages chosen on the next form',
  },

  creditsModal: {
    title: 'Buy credits',
    dialogAria: 'Buy credits',
    balancePrefix: 'Current balance:',
    creditsSuffix: 'credits',
    packageUnit: 'credits',
    info: 'Credits are used for book translations.',
    needMore: (amount: string) => `This book needs ${amount} more credits`,
    checkoutFailed: "Couldn't open the payment page",
    added: (amount: string) => `${amount} credits added`,
    addedNoAmount: 'Payment complete — your credits are in',
    pending:
      'Your payment is still processing. Credits are added when it completes.',
  },

  reader: {
    loadingChapter: 'Loading chapter…',
    prevChapter: 'Previous chapter',
    nextChapter: 'Next chapter',
    menu: 'Menu',
    tapToToggle: 'Tap to toggle translation',
    note: 'Note',
    noteNotFound: 'Note content not found',
    viewInContext: 'View in context',
    returnToReading: 'Return to reading',
    backToShelf: 'Back to shelf',
    showTranslation: 'Show translation',
    showOriginal: 'Show original',
    chapters: 'Chapters',
    markAsRead: 'Mark as read',
    share: 'Share',
    copyShareLink: 'Copy share link',
    copied: 'Copied',
    revokeShare: 'Revoke share',
    theme: 'Theme',
    themeAuto: 'Auto (follow system)',
    themeAutoShort: 'Auto',
    themeNames: {
      white: 'White',
      paper: 'Paper',
      sepia: 'Sepia',
      green: 'Green',
      dark: 'Dark',
    } as Record<string, string>,
    firstLineIndent: 'First-line indent',
    typography: 'Typography',
    font: 'Font',
    fontNames: {
      song: 'Song',
      kai: 'Kai',
      sans: 'Sans',
    } as Record<string, string>,
    fontSize: 'Font size',
    paragraphGap: 'Paragraph gap',
    lineHeight: 'Line height',
    letterSpacing: 'Letter spacing',
    wordSpacing: 'Word spacing',
    fontWeight: 'Font weight',
    // Accessible names for the − / + buttons beside each setting.
    decrease: (setting: string) => `Decrease ${setting.toLowerCase()}`,
    increase: (setting: string) => `Increase ${setting.toLowerCase()}`,
    // Shown for a typography setting at its default value.
    defaultValue: 'Default',
    resetToDefault: 'Reset to default',
    contents: 'Contents',
    search: 'Search',
    searchPlaceholder: 'Search original & translation…',
    searching: 'Searching…',
    searchNoResults: 'No matches found',
  },

  auth: {
    continueWithGoogle: 'Continue with Google',
    emailPlaceholder: 'you@example.com',
    sendCode: 'Email me a sign-in code',
    orEmail: 'or continue with email',
    codeSentLabel: 'Code sent to',
    codePlaceholder: '6-digit code',
    verifyAndSignIn: 'Sign in',
    changeEmail: 'Use a different email',
    sendCodeFailed: 'Failed to send the code',
    verifyFailed: 'Sign-in failed',
  },

  book: {
    loadingContent: 'Loading book content…',
    loadChapterFailed: "Couldn't load this chapter",
    loadFailedMessage: 'Check your connection and try again.',
    notFoundTitle: 'Book not found',
    notFoundMessage:
      'It may have been removed, or you may need to sign in to open it.',
    markReadFailed: "Couldn't save. Please try again.",
    createShareFailed: 'Failed to create share link',
    revokeShareFailed: 'Failed to revoke share',
  },

  shared: {
    linkInvalid: 'This link no longer works',
    linkInvalidMessage: 'The owner may have stopped sharing this book.',
    loadBookFailed: "Couldn't load the book",
    loadChapterFailed: "Couldn't load this chapter",
    goToLibrary: 'Go to library',
    loadingSharedBook: 'Loading shared book…',
  },

  errors: {
    somethingWentWrong: 'Something went wrong',
    unexpectedError: 'An unexpected error occurred. Please try again.',
    tryAgain: 'Try again',
    fetchBooksFailed: 'Failed to fetch books',
    deleteBookFailed: "Couldn't remove the book",
    saveLabelFailed: "Couldn't save the label",
    moveBookFailed: "Couldn't move the book",
  },

  pwa: {
    newVersion: 'A new version is available',
    refresh: 'Refresh',
    updating: 'Updating…',
  },

  // Display names for translation source/target languages (keys are the
  // codes in SUPPORTED_LANGUAGES).
  languageNames: {
    zh: 'Chinese',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    ja: 'Japanese',
    ko: 'Korean',
    ru: 'Russian',
    en: 'English',
  } as Record<string, string>,
};

export type Messages = typeof en;
